/* eslint-disable ordered-imports/ordered-imports */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ADIColors from "@NA/Utils/ADIColors";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps } from "styled-components";
import toggleLoginDrawer from "@NA/Handlers/ADILoginDrawerHandler";
import { connect, ResolveThunks } from "react-redux";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { pinkBase } from "@optimizely/design-tokens/dist/js/colors";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import GridContainer, { GridOffset, GridWrapper } from "@insite/mobius/GridContainer";

import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

export interface PanelMenuPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<PanelMenuPresentationProps>;
        children?: StyledProp<PanelMenuPresentationProps>;
        menu?: StyledProp<PanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
}

export type PanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        dispatch?: any;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        isSolutionandServices?: boolean;
        currentState?: CustomApplicationState;
    }
>;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
    }
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    height: calc(100% - 50px);
    overflow-y: auto;
    margin-top: -1px;
    background: ${({ color, theme }) => resolveColor(color, theme)};
    @media only screen and (max-width: 1080px) {
        height: calc(100% - 140px);
    }
    @media only screen and (max-width: 710px) {
        height: calc(100% - 50px);
    }
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    z-index: 1;
    transition: all ease-in-out ${getProp("theme.transition.duration.short")}ms;
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
        `;
    }}
    ${injectCss}
`;

const DrawerHeaderButtonWrapper = styled.div<any>`
    padding: 5px 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    gap: 5px;
`;
const BackButtonWrapper = styled.div<any>`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    font-size: 14px;
                    line-height: 22px;
                    color: rgb(45, 41, 38);
                `,
                css`
                    font-size: 14px;
                    line-height: 22px;
                    color: rgb(45, 41, 38);
                `,
                css``,
                css``,
                css``,
            ],
            "max",
        )}
`;

export interface PanelMenuStyles {
    panelRow?: InjectableCss;
    menuitemlist?: GridItemProps;
    leftMegaMenuLink: LinkPresentationProps;
    solutionsandserviceslabel?: TypographyProps;
    solutionslabel?: TypographyProps;
    menucontainer?: InjectableCss;
    solutionslist?: InjectableCss;
    servicesparent?: InjectableCss;
    solutionsparent?: InjectableCss;
    externalLink?: InjectableCss;
    shopbtnstyle?: InjectableCss;
    submenus?: InjectableCss;
    closeIcon?: InjectableCss;
}

export const panelMenuStyles: PanelMenuStyles = {
    panelRow: {
        css: css`
            &:active {
                background: transparent !important;
            }
            background: transparent !important;
            padding: 0px 20px !important;
            line-height: 38px !important;
            height: 38px !important;
        `,
    },
    closeIcon: {
        css: css`
            width: 24px;
            height: 24px;
            @media only screen and (max-width: 1080px) {
                width: 24px;
                height: 24px;
            }
        `,
    },

    leftMegaMenuLink: {
        css: css`
            > span {
                color: ${getColor("text.main")} !important;
            }
            &:hover {
                > span {
                    text-decoration: underline;
                }
            }
        `,
    },

    externalLink: {
        css: css`
            margin-bottom: 0px;
            > span {
                color: ${getColor("text.main")} !important;
            }
            &:hover {
                > span {
                    text-decoration: underline;
                }
            }
        `,
    },

    menucontainer: {
        css: css`
            display: block !important;
            background: none !important;
            padding: 0 20px;
            max-height: calc(100vh - 70px);
            min-height: initial;
            height: calc(100% - 70px);
        `,
    },
    submenus: {
        css: css`
            font-size: 12px;
            line-height: 17px;
            color: rgb(45, 41, 38) !important;
        `,
    },

    solutionsparent: {
        css: css`
            display: flex;
            max-width: 100%;
            flex-basis: 100%;
            margin-top: 10px;
            flex-direction: column;
        `,
    },

    servicesparent: {
        css: css`
            display: flex;
            max-width: 100%;
            flex-basis: 100%;
            margin-top: 20px;
            flex-direction: column;
        `,
    },

    solutionsandserviceslabel: {
        css: css`
            font-family: "Nunito Sans", sans-serif;
            font-size: 14px !important;
            font-weight: 600 !important;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.43 !important;
            letter-spacing: normal;
            text-align: left;
            color: #2d2926;
            text-transform: uppercase;
            text-decoration: none !important;
            cursor: default;
            padding: 15px;
        `,
    },
    solutionslabel: {
        css: css`
            font-family: "Nunito Sans", sans-serif;
            font-size: 12px !important;
            font-weight: bold !important;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            text-align: left;
            color: #2d2926;
            text-transform: none;
            padding: 15px;
            width: 100%;
        `,
    },

    solutionslist: {
        css: css`
            font-family: "Nunito Sans", sans-serif;
            font-size: 12px !important;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: normal !important;
            letter-spacing: normal;
            text-align: left;
            color: #2d2926;
            text-transform: none;
            padding-top: 11px;
            padding-bottom: 11px;
        `,
    },
    shopbtnstyle: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 14px;
                            line-height: 20px;
                            font-weight: 600;
                        `,
                        css`
                            font-size: 14px;
                            line-height: 20px;
                            font-weight: 600;
                        `,
                        css``,
                        css``,
                        css``,
                    ],
                    "max",
                )}
        `,
    },
};

const cssStyles = panelMenuStyles;

const mapStateToProps = (state: CustomApplicationState) => {
    const customerSettings = getSettingsCollection(state).customerSettings;
    return {
        session: state.context.session,
        currentState: state,
        userRoles: state.ADIGlobalData.ADIUserRoleStore,
        location: getLocation(state),
        userloyaltyState: state.ADIGlobalData?.loyaltyRewardUserStore,
        hmiRewardsSettingSettings: state.context.settings.settingsCollection.hmiRewardsSettingSettings,
        xtenMenuName: customerSettings.xtenMenuName,
        xtenAllowedUserRole: customerSettings.allowedUserRole,
        isPendingVerification: state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    };
};
const mapDispatchToProps = {
    toggleLoginDrawer,
};

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type PanelMenuProps = PanelMenuComponentProps & PanelMenuPresentationProps;

export interface PanelMenuExtended extends PanelMenuComponentProps {
    maxDepth: number;
}

type PanelMenuPropsCompleted = PanelMenuPresentationProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    PanelMenuExtended;
// Removed and created new interface above with the name PanelMenuExtended

// Omit<PanelMenuComponentProps, "maxDepth"> &
// Required<Pick<PanelMenuComponentProps, "maxDepth">>;

class PanelMenu extends React.Component<PanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
    };

    UNSAFE_componentWillReceiveProps(nextProps: PanelMenuPropsCompleted) {
        // eslint-disable-line camelcase
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => { });
        }
    }

    moveNextLeft = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer - 1 };
        });
    };

    moveNextRight = () => {
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };

    toggledrawer = () => {
        this.props.toggleLoginDrawer({ loginDrawer: true });
    };

    checkIfUrlAsExternlLinks = (url: string) => {
        const externalLinks = ["/additional-learnings/video-center"];
        return externalLinks.includes(url);
    };

    naviagteToOtherWindow = (url: string) => {
        window.open(this.getExternlUrl(url), "_blank");
        if (this.props.closeOverlay) {
            this.props.closeOverlay();
        }
    };

    getExternlUrl = (inputString: string) => {
        if (inputString == "/additional-learnings/video-center") {
            return "https://videos.adiglobal.com/video-center";
        }
        return "/";
    };

    renderPath = (inputString: string) => {
        const parts = inputString.split("/");
        const result = `/${parts[2]}`;
        if (result == "/training-events") {
            return "/training-events";
        }
        return result;
    };

    solutionandservicesMenuItems(menuitems: MappedLink[]) {
        const pages: any = this.props.currentState.links.pageLinks[0].children;
        let solutionsMenuArray: any = [];
        let servicesMenuArray: any = [];

        let additionalLearningArray: any = [];

        const solutionItems = pages.filter((item: { url: string }) => item?.url?.toLowerCase().trim() === "/solutions");
        if (solutionItems && solutionItems[0].children) {
            solutionsMenuArray = solutionItems[0].children.filter((child: any) => !child.excludeFromNavigation);
        }

        const serviceItems = pages.filter((item: { url: string }) => item?.url?.toLowerCase().trim() === "/services");

        if (serviceItems && serviceItems[0].children) {
            servicesMenuArray = serviceItems[0].children.filter((child: any) => !child.excludeFromNavigation);
        }

        const addtionalLearningItems = pages.filter(
            (item: { url: string }) => item?.url?.toLowerCase().trim() === "/additional-learnings",
        );

        if (addtionalLearningItems && addtionalLearningItems.length && addtionalLearningItems[0].children) {
            additionalLearningArray = addtionalLearningItems[0].children.filter(
                (child: any) => !child.excludeFromNavigation,
            );
        }
        const session = this.props.session;
        const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
        const userloyaltyState = this.props.userloyaltyState;
        const userRoles = session && session.userRoles;
        const isProRewardsUser = userRoles && userRoles.includes("ADIProRewards");
        const userNameSession = session && session.userName?.toLocaleLowerCase();
        const emailLTY =
            userloyaltyState.user.email != undefined ? userloyaltyState.user.email?.toLocaleLowerCase() : "";
        const isRewardsInvitedUser =
            userloyaltyState.user.isEnrolled == false &&
            userNameSession == emailLTY &&
            !isProRewardsUser &&
            (userloyaltyState.user.status?.toLocaleUpperCase() == "A" ||
                userloyaltyState.user.status?.toLocaleUpperCase() == "B" ||
                userloyaltyState.user.status?.toLocaleUpperCase() == "O");
        const hmiRewardsSettingSettings: any = this.props.hmiRewardsSettingSettings;

        const checkRewardsInvitedUser = (item: any) => {
            if (isAuthenticated && isRewardsInvitedUser) {
                return hmiRewardsSettingSettings.hmiActivatePageUrl;
            } else return item.url;
        };

        const isXtenRole = userRoles && userRoles.includes(this.props.xtenAllowedUserRole);
        const xtenMenuName = this.props.xtenMenuName;
        return (
            <>
                <GridContainer {...cssStyles.menucontainer}>
                    <Typography {...cssStyles.solutionsandserviceslabel}>
                        {translate("Solutions & Services")}
                    </Typography>
                    <StyledWrapper {...cssStyles.solutionsparent}>
                        <Typography
                            {...cssStyles.solutionslabel}
                            onClick={() => {
                                window.location.href = "/solutions";
                            }}
                        >
                            {translate("Solutions")}
                        </Typography>
                        {solutionsMenuArray &&
                            solutionsMenuArray.map((item: any, index: number) => (
                                <GridItem {...cssStyles.solutionslist} width={12 as GridWidths} key={index}>
                                    <Link
                                        {...cssStyles.leftMegaMenuLink}
                                        href={item.url}
                                        onClick={() => {
                                            if (this.props.closeOverlay) {
                                                this.props.closeOverlay();
                                            }
                                        }}
                                    >
                                        <Typography>{item.title}</Typography>
                                    </Link>
                                </GridItem>
                            ))}
                    </StyledWrapper>
                    <StyledWrapper {...cssStyles.servicesparent}>
                        <Typography
                            {...cssStyles.solutionslabel}
                            onClick={() => {
                                window.location.href = "/services";
                            }}
                        >
                            {translate("Services")}
                        </Typography>
                        {servicesMenuArray &&
                            servicesMenuArray.map(
                                (item: any) =>
                                    (item?.title?.toLocaleLowerCase() !== xtenMenuName?.toLocaleLowerCase() ||
                                        (isAuthenticated &&
                                            isXtenRole &&
                                            item?.title?.toLocaleLowerCase() ===
                                            xtenMenuName?.toLocaleLowerCase())) && (
                                        <GridItem {...cssStyles.solutionslist} width={12}>
                                            <Link
                                                {...cssStyles.leftMegaMenuLink}
                                                href={
                                                    isAuthenticated &&
                                                        item?.title?.toLocaleLowerCase() === "adi pro rewards"
                                                        ? checkRewardsInvitedUser(item)
                                                        : item?.url
                                                }
                                                onClick={() => {
                                                    if (this.props.closeOverlay) {
                                                        this.props.closeOverlay();
                                                    }
                                                }}
                                            >
                                                <Typography>
                                                    {item?.title?.toLocaleLowerCase() ===
                                                        xtenMenuName?.toLocaleLowerCase() && isXtenRole
                                                        ? xtenMenuName
                                                        : item?.title}
                                                </Typography>
                                            </Link>
                                        </GridItem>
                                    ),
                            )}
                    </StyledWrapper>
                    <StyledWrapper {...cssStyles.servicesparent}>
                        <Typography {...cssStyles.solutionslabel}>{translate("Additional Learning")}</Typography>
                        {additionalLearningArray &&
                            additionalLearningArray.map((item: any) => (
                                <GridItem {...cssStyles.solutionslist} width={12}>
                                    {!this.checkIfUrlAsExternlLinks(item.url) ? (
                                        <Link
                                            {...cssStyles.leftMegaMenuLink}
                                            href={this.renderPath(item.url)}
                                            data-category-name={`selectedCategoryName_${item.title}`}
                                            onClick={() => {
                                                if (this.props.closeOverlay) {
                                                    this.props.closeOverlay();
                                                }
                                            }}
                                        >
                                            <Typography>{item.title}</Typography>
                                        </Link>
                                    ) : (
                                        <Link
                                            onClick={() => this.naviagteToOtherWindow(item.url)}
                                            className="solution-mega-menu-left-link"
                                            {...cssStyles.externalLink}
                                            data-category-name={`selectedCategoryName_${item.title}`}
                                        >
                                            <Typography>{item.title}</Typography>
                                        </Link>
                                    )}
                                </GridItem>
                            ))}
                    </StyledWrapper>
                </GridContainer>
            </>
        );
    }

    renderMenuItems(
        childMenuItems: MappedLink[],
        currentDepth: number,
        propBuilder: any,
        menuTitle: string,
        isSolutionandServices: boolean,
    ) {
        const isPR = window.location.hostname.endsWith(".pr");
        const { applyProp } = propBuilder;
        const { currentUrl } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");
        const isAuthenticated =
            (this.props.session.isAuthenticated || this.props.session.rememberMe) && !this.props.session.isGuest;

        const displayChildMenuItems = (menuitems: MappedLink[]) => {
            const isSolutionandServicesMenuItems =
                menuitems && menuitems.map(item => item.url).filter((item: any) => item.includes("solutions"));
            if (isSolutionandServicesMenuItems.length) {
                return menuitems;
            }
            const otherChildMenu = menuitems.filter(item => !item.excludeFromNavigation);
            return otherChildMenu;
        };
        const isProductInventoryOnlyRoleValue =
            this.props.userRoles.productInventoryOnly ||
            (this.props.userRoles.productInventoryOnly && this.props.userRoles.nationalInventory);
        return (
            <>
                {displayChildMenuItems(childMenuItems).map(item => {
                    const renderChildren =
                        item.children?.filter(child => !child.excludeFromNavigation).length &&
                        currentDepth < this.props.maxDepth;

                    const clickableProps: { href?: string } = { ...item.clickableProps };
                    if (!renderChildren && item.url) {
                        let sort = "";
                        if (!item.url.includes("Deals/sale-center")) {
                            sort = `?sort=Bestseller`;
                        }
                        clickableProps.href = `${item.url}${sort}`;
                    }

                    const menuItem = (
                        <PanelRow
                            key={item.title}
                            isCurrent={currentUrl === item.url}
                            onClick={() => {
                                if (item.title === "ADI Pro Rewards") {
                                    this.props.dispatch({
                                        type: "AccountPage/ProRewardsUpdate",
                                    });
                                }
                                if (this.props.closeOverlay) {
                                    this.props.closeOverlay();
                                }
                            }}
                            color={applyProp("bodyColor")}
                            hasChildren={renderChildren || false}
                            tabIndex={this.state.layer === 0 ? 0 : -1}
                            {...clickableProps}
                            {...panelRowProps}

                            title={menuTitle}
                            {...cssStyles.panelRow}
                        >
                            <Typography
                                data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                weight="bold"
                                size={15}
                                {...typographyProps}
                                {...cssStyles.submenus}
                                style={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    color: ADIColors.darkBlack5,
                                }}
                            >
                                {item.title}
                            </Typography>
                        </PanelRow>
                    );
                    if (renderChildren) {
                        return (
                            <PanelMenu
                                {...this.props}
                                as="div"
                                key={item.title}
                                panelTrigger={menuItem}
                                menuItems={item.children as MappedLink[]}
                                maxDepth={this.props.maxDepth - 1}
                                layer={this.state.layer}
                            />
                        );
                    }
                    return menuItem;
                })}
                {/* //ADINA-67866 */}
                {/* {!isProductInventoryOnlyRoleValue &&
                this.props?.panelTrigger?.props?.children?.props?.children === "Deals" && !isPR ? (
                    <PanelRow
                        key="MonthlyFlyer"
                        onClick={() => {
                            if (isAuthenticated) {
                                // this.props.history.push("/myaccount/monthlyflyer");
                                window.location.href = "/myaccount/monthlyflyer";
                            } else {
                                this.toggledrawer && this.toggledrawer();
                                window.sessionStorage.setItem("redirectAfterLogin", "/myaccount/monthlyflyer");
                            }
                            if (this.props.closeOverlay) {
                                this.props.closeOverlay();
                            }
                        }}
                        color={applyProp("bodyColor")}
                        tabIndex={this.state.layer === 0 ? 0 : -1}
                        {...panelRowProps}
                        style={{ padding: "15px 20px 10px 20px" }}
                        title={menuTitle}
                        {...cssStyles.panelRow}
                    >
                        {!this.props.isPendingVerification && (
                            <Typography
                                data-test-selector={`${this.props["data-test-selector"]}_${"Monthly Sales Flyer"}`}
                                weight="bold"
                                size={15}
                                {...typographyProps}
                                style={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    color: ADIColors.darkBlack5,
                                }}
                            >
                                {translate("Monthly Sales Flyer")}
                            </Typography>
                        )}
                    </PanelRow>
                ) : null} */}
            </>
        );
    }

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        currentUrl,
                        descriptionId,
                        menuItems,
                        isSolutionandServices,
                        panelTrigger,
                        closeOverlay,
                        ...otherProps
                    } = this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { spreadProps, applyProp } = propBuilder;
                    const cssOverrides = spreadProps("cssOverrides");
                    const headerColor = applyProp("headerColor", "common.backgroundContrast");
                    const renderMoreChildren = this.props.menuItems?.filter((item: any) => item?.children?.length > 0);
                    return (
                        <PanelMenuWrapper
                            css={cssOverrides?.wrapper}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={cssOverrides?.children}
                                data-layer={layer}
                            >
                                <PanelRow
                                    style={{ paddingTop: "15px", backgroundColor: ADIColors.gray6 }}
                                    as="div"
                                    header
                                >
                                    <DrawerHeaderButtonWrapper
                                        style={{ width: "calc(100% - 55px)", justifyContent: "left" }}
                                    >
                                        <ButtonIcon
                                            {...spreadProps("backIconProps")}
                                            onClick={this.moveNextRight}
                                            {...cssStyles.closeIcon}
                                        />
                                        <BackButtonWrapper>
                                            Back to {panelTrigger?.props?.title}{" "}
                                            {renderMoreChildren?.length
                                                ? panelTrigger?.props?.title === "Main Menu"
                                                    ? ""
                                                    : "Menu"
                                                : ""}
                                        </BackButtonWrapper>
                                        <VisuallyHidden>{translate("back")}</VisuallyHidden>
                                    </DrawerHeaderButtonWrapper>
                                    {closeOverlay && (
                                        <DrawerHeaderButtonWrapper>
                                            <ButtonIcon
                                                {...spreadProps("closeIconProps")}
                                                onClick={closeOverlay}
                                                {...cssStyles.closeIcon}
                                            />
                                            <VisuallyHidden>{translate("close")}</VisuallyHidden>
                                        </DrawerHeaderButtonWrapper>
                                    )}
                                </PanelRow>
                                <PanelMenuStyle
                                    css={cssOverrides?.menu}
                                    color={applyProp("bodyColor", "common.accent")}
                                >
                                    {menuItems ? (
                                        <>
                                            {panelTrigger?.props?.children?.props?.children === "Shop" ? (
                                                <StyledWrapper
                                                    {...cssStyles.shopbtnstyle}
                                                    style={{ padding: "15px 20px 10px" }}
                                                >
                                                    <Typography as="span" style={{ textTransform: "uppercase" }}>
                                                        {translate("Shop")}
                                                    </Typography>
                                                </StyledWrapper>
                                            ) : null}
                                            {isSolutionandServices ? (
                                                <> {this.solutionandservicesMenuItems(menuItems)}</>
                                            ) : (
                                                this.renderMenuItems(
                                                    menuItems,
                                                    1,
                                                    propBuilder,
                                                    panelTrigger?.props?.children?.props?.children,
                                                    isSolutionandServices as boolean,
                                                )
                                            )}
                                        </>
                                    ) : (
                                        <>{this.props.children}</>
                                    )}
                                </PanelMenuStyle>
                            </PanelChildren>
                        </PanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */
// TODO ISC-11903
// export const PanelMenu = (props: PanelMenuProps) => (<PanelMenuCC {...props as PanelMenuPropsCompleted} />);

PanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default connect(mapStateToProps, mapDispatchToProps)(PanelMenu);
